(() => {
  'use strict';

  const initialized = new WeakSet();
  let guideNumber = 0;
  const textOf = (node) => (node?.textContent || '').replace(/\s+/g, ' ').trim();
  const sizeNumber = (value) => {
    const match = String(value).trim().match(/^(?:child\s*)?0*(\d{1,2})$/i);
    return match ? Number(match[1]) : null;
  };
  const unitOf = (text) => {
    const inch = /\b(?:inch(?:es)?|in)\b/i.test(text);
    const cm = /\b(?:cm|centimet(?:er|re)s?)\b/i.test(text);
    return inch === cm ? null : inch ? 'inch' : 'cm';
  };

  // Read the merchant's tables, including tables nested in legacy .size_con wrappers.
  // The original description remains untouched and works without JavaScript.
  function findCharts(description) {
    if (!description) return [];
    let precedingHeading = '';
    const charts = [];
    description.querySelectorAll('h1,h2,h3,h4,h5,h6,table').forEach((node) => {
      if (node.tagName !== 'TABLE') {
        if (textOf(node)) precedingHeading = textOf(node);
        return;
      }
      const rows = Array.from(node.rows);
      if (rows.length < 2) return;
      const headings = Array.from(rows[0].cells, textOf).join(' ');
      const hasSizeHeader = /US\s*Size\s*No\.?/i.test(headings);
      const hasMeasurements = /\bbust\b/i.test(headings) && /\bwaist\b/i.test(headings) && /hollow\s*to\s*floor/i.test(headings);
      const underSizeHeading = /size\s*chart/i.test(precedingHeading);
      const hasChildRows = rows.slice(1).some((row) => sizeNumber(textOf(row.cells[0])) !== null && row.cells.length > 1);
      if (!(hasSizeHeader && hasMeasurements) && !(underSizeHeading && hasChildRows)) return;
      const headingUnit = underSizeHeading ? unitOf(precedingHeading) : null;
      const cellUnit = unitOf(rows.slice(1).map(textOf).join(' '));
      charts.push({ source: node, unit: headingUnit || cellUnit, hasSizeHeader });
    });
    return charts;
  }

  function makeChart(chart, captionText) {
    const table = document.createElement('table');
    table.className = 'size-guide__table';
    const caption = table.createCaption();
    caption.textContent = captionText;
    const body = table.createTBody();
    Array.from(chart.source.rows).forEach((sourceRow, rowIndex) => {
      const row = body.insertRow();
      const firstText = textOf(sourceRow.cells[0]);
      const rowSize = rowIndex > 0 ? sizeNumber(firstText) : null;
      if (rowSize !== null) row.dataset.chartSize = String(rowSize);
      Array.from(sourceRow.cells).forEach((sourceCell, cellIndex) => {
        const isColumnHeader = rowIndex === 0;
        const isRowHeader = cellIndex === 0 && rowSize !== null;
        const cell = document.createElement(isColumnHeader || isRowHeader ? 'th' : 'td');
        // Text-only copies prevent legacy widths, duplicate IDs or embedded content
        // from affecting the accessible dialog. Measurement values are not converted.
        cell.textContent = textOf(sourceCell);
        if (isColumnHeader || isRowHeader) cell.scope = isColumnHeader ? 'col' : 'row';
        if (sourceCell.colSpan > 1) cell.colSpan = sourceCell.colSpan;
        if (sourceCell.rowSpan > 1) cell.rowSpan = sourceCell.rowSpan;
        row.append(cell);
      });
    });
    return table;
  }

  function updateSelection(root, container) {
    const selectedText = textOf(root.querySelector('[data-selected-size]'));
    const selectedSize = sizeNumber(selectedText);
    let matches = 0;
    container.querySelectorAll('[data-chart-size]').forEach((row) => {
      const matched = selectedSize !== null && Number(row.dataset.chartSize) === selectedSize;
      row.classList.toggle('is-selected', matched);
      if (matched) {
        row.setAttribute('aria-current', 'true');
        matches += 1;
      } else row.removeAttribute('aria-current');
    });
    const selection = container.querySelector('.size-guide__selection');
    if (selection) {
      selection.hidden = !matches;
      selection.textContent = matches ? `Your selected size: ${selectedText}` : '';
    }
  }

  function createInlineGuide(root, mount, charts) {
    mount.classList.add('inline-size-guide');
    if (!charts.length) {
      mount.innerHTML = `<div class="inline-size-guide__empty"><p class="inline-size-guide__eyebrow">A little guidance</p><h3>Need help with sizing?</h3><p>Our team can help you choose a size for her.</p><a href="/pages/contact-us#sizing">Ask us about sizing <span aria-hidden="true">↗</span></a></div>`;
      mount.hidden = false;
      return;
    }
    const prefix = `inline-size-guide-${++guideNumber}`;
    mount.setAttribute('role', 'region');
    mount.setAttribute('aria-labelledby', `${prefix}-title`);
    mount.innerHTML = `
      <div class="inline-size-guide__header">
        <div><p class="inline-size-guide__eyebrow">Find her fit</p><h3 id="${prefix}-title">Size chart</h3><p class="inline-size-guide__intro">Compare her measurements with the chart below.</p></div>
        <div class="inline-size-guide__tools"><div class="size-guide__tabs" role="tablist" aria-label="Size chart measurement units"></div><p class="size-guide__selection" aria-live="polite" hidden></p></div>
      </div>
      <p class="size-guide__scroll-hint">Swipe the chart to see all measurements <span aria-hidden="true">↔</span></p>
      <div class="size-guide__panels"></div>
      <p class="size-guide__help">Between sizes or need advice? <a href="/pages/contact-us#sizing">Ask us about sizing <span aria-hidden="true">↗</span></a></p>`;
    const tabs = mount.querySelector('.size-guide__tabs');
    const panels = mount.querySelector('.size-guide__panels');
    const buttons = [];
    const chartPanels = [];
    const labelsSeen = new Map();
    const selectChart = (index, moveFocus = false) => {
      buttons.forEach((button, i) => {
        button.setAttribute('aria-selected', String(i === index));
        button.tabIndex = i === index ? 0 : -1;
        chartPanels[i].hidden = i !== index;
      });
      if (moveFocus) buttons[index].focus();
    };
    charts.forEach((chart, index) => {
      const unitLabel = chart.unit === 'inch' ? 'Inches' : chart.unit === 'cm' ? 'Centimetres' : 'Size chart';
      const labelCount = (labelsSeen.get(unitLabel) || 0) + 1;
      labelsSeen.set(unitLabel, labelCount);
      const button = document.createElement('button');
      button.type = 'button';
      button.id = `${prefix}-tab-${index}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', `${prefix}-panel-${index}`);
      button.textContent = labelCount > 1 ? `${unitLabel} ${labelCount}` : unitLabel;
      button.addEventListener('click', () => selectChart(index));
      button.addEventListener('keydown', (event) => {
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = buttons.length - 1;
        else return;
        event.preventDefault();
        selectChart(next, true);
      });
      const panel = document.createElement('div');
      panel.id = `${prefix}-panel-${index}`;
      panel.className = 'size-guide__panel';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', button.id);
      panel.tabIndex = 0;
      panel.append(makeChart(chart, `Size chart${chart.unit ? ` — ${unitLabel.toLowerCase()}` : ''}`));
      tabs.append(button);
      panels.append(panel);
      buttons.push(button);
      chartPanels.push(panel);
    });
    selectChart(0);
    updateSelection(root, mount);
    root.addEventListener('maria:variant-change', () => updateSelection(root, mount));
    mount.hidden = false;
  }

  function createGuide(root, charts) {
    const prefix = `product-size-guide-${++guideNumber}`;
    const dialog = document.createElement('dialog');
    dialog.className = 'size-guide';
    dialog.setAttribute('aria-labelledby', `${prefix}-title`);
    dialog.setAttribute('aria-describedby', `${prefix}-intro`);
    dialog.innerHTML = `
      <div class="size-guide__content">
        <button class="size-guide__close" type="button" aria-label="Close size guide" autofocus><span aria-hidden="true">×</span></button>
        <p class="size-guide__eyebrow">Size guide</p>
        <h2 class="size-guide__title" id="${prefix}-title">Find her fit</h2>
        <p class="size-guide__intro" id="${prefix}-intro">Measure her bust, waist and height before choosing a size. The chart also lists hollow-to-floor length.</p>
        <p class="size-guide__selection" aria-live="polite" hidden></p>
        <div class="size-guide__tabs" role="tablist" aria-label="Measurement units"></div>
        <p class="size-guide__scroll-hint">Swipe the chart to see all measurements.</p>
        <div class="size-guide__panels"></div>
        <p class="size-guide__help">Need a little guidance? <a href="/pages/contact-us#sizing">Ask us about sizing <span aria-hidden="true">↗</span></a></p>
      </div>`;
    const tabs = dialog.querySelector('.size-guide__tabs');
    const panels = dialog.querySelector('.size-guide__panels');
    const buttons = [];
    const chartPanels = [];
    const labelsSeen = new Map();
    let lastTrigger = null;
    let backdropPointer = false;

    function selectChart(index, moveFocus = false) {
      buttons.forEach((button, i) => {
        button.setAttribute('aria-selected', String(i === index));
        button.tabIndex = i === index ? 0 : -1;
        chartPanels[i].hidden = i !== index;
      });
      if (moveFocus) buttons[index].focus();
    }

    charts.forEach((chart, index) => {
      const unitLabel = chart.unit === 'inch' ? 'Inches' : chart.unit === 'cm' ? 'Centimetres' : 'Size chart';
      const labelCount = (labelsSeen.get(unitLabel) || 0) + 1;
      labelsSeen.set(unitLabel, labelCount);
      const label = labelCount > 1 ? `${unitLabel} ${labelCount}` : unitLabel;
      const button = document.createElement('button');
      button.type = 'button';
      button.id = `${prefix}-tab-${index}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', `${prefix}-panel-${index}`);
      button.textContent = label;
      button.addEventListener('click', () => selectChart(index));
      button.addEventListener('keydown', (event) => {
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = buttons.length - 1;
        else return;
        event.preventDefault();
        selectChart(next, true);
      });
      const panel = document.createElement('div');
      panel.id = `${prefix}-panel-${index}`;
      panel.className = 'size-guide__panel';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', button.id);
      panel.tabIndex = 0;
      panel.append(makeChart(chart, `Size chart${chart.unit ? ` — ${unitLabel.toLowerCase()}` : ''}`));
      tabs.append(button);
      panels.append(panel);
      buttons.push(button);
      chartPanels.push(panel);
    });
    selectChart(0);

    dialog.querySelector('.size-guide__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('pointerdown', (event) => { backdropPointer = event.target === dialog; });
    dialog.addEventListener('click', (event) => {
      if (backdropPointer && event.target === dialog) dialog.close();
      backdropPointer = false;
    });
    // Native dialog handles Escape and traps focus. Explicit restoration also covers
    // the close button and backdrop without moving focus on a subsequent page load.
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('size-guide-is-open');
      if (lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
    });
    document.body.append(dialog);

    return {
      update() { updateSelection(root, dialog); },
      open(trigger) {
        updateSelection(root, dialog);
        lastTrigger = trigger;
        dialog.showModal();
        document.documentElement.classList.add('size-guide-is-open');
      },
      remove() {
        if (dialog.open) dialog.close();
        dialog.remove();
      }
    };
  }

  function initialize(scope = document) {
    const roots = scope.matches?.('[data-product-root]') ? [scope] : scope.querySelectorAll('[data-product-root]');
    roots.forEach((root) => {
      if (initialized.has(root)) return;
      initialized.add(root);
      const inlineMount = root.querySelector('[data-inline-size-guide]');
      if (inlineMount) createInlineGuide(root, inlineMount, findCharts(root.querySelector('#product-details .pdp-description')));
      let guide = null;
      root.addEventListener('maria:variant-change', () => guide?.update());
      root.addEventListener('click', (event) => {
        const trigger = event.target.closest('a[data-size-guide]');
        if (!trigger || !root.contains(trigger) || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const description = root.querySelector('#product-details .pdp-description');
        const charts = findCharts(description);
        if (!charts.length || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) {
          if (!inlineMount || inlineMount.hidden) {
            const details = root.querySelector('#product-details .pdp-full-details');
            if (details) details.open = true;
          }
          return;
        }
        event.preventDefault();
        if (!guide) guide = createGuide(root, charts);
        guide.open(trigger);
      });
      (root.closest('.shopify-section') || root).addEventListener('shopify:section:unload', () => guide?.remove());
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
